import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Stack,
    Button,
    TextField,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    InputAdornment,
    FormControlLabel,
    Switch,
    Snackbar
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Refresh from '@mui/icons-material/Refresh';
import People from '@mui/icons-material/People';
import VpnKey from '@mui/icons-material/VpnKey';
import Save from '@mui/icons-material/Save';
import { BasicPage } from '../components/BasicPage';
import { useAuth } from '../hooks/useAuth';
import { getFirestore, collection, getDocs, addDoc, setDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import firebaseConfig from '../config/firebaseConfig';

/**
 * PunchoutClientManagement Component
 * 
 * Manages punchout clients directly in Firestore (Frontend-only)
 * Allows SuperAdmins to create, edit, and delete punchout client configurations
 */
export const PunchoutClientManagement = () => {
    const { user, claims } = useAuth();
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [showSecret, setShowSecret] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Form state
    const [formData, setFormData] = useState({
        clientId: '',
        sharedSecret: '',
        accountID: '',
        domain: '',
        name: '',
        active: true,
        userEmail: ''
    });
    
    const [errors, setErrors] = useState({});

    const [profileSecret, setProfileSecret] = useState('');
    const [profileUrls, setProfileUrls] = useState({ profileUrl: '', punchoutSetupUrl: '' });
    const [showProfileSecret, setShowProfileSecret] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [savingProfile, setSavingProfile] = useState(false);

    const db = getFirestore();
    const punchoutInitializeUrl = `${firebaseConfig.siteUrl}/app/punchout/initialize`;
    const simpleSystemsOciUrl = (clientId, sharedSecret) =>
        `${firebaseConfig.siteUrl}/login.php?func=login&username=${encodeURIComponent(clientId)}&password=${encodeURIComponent(sharedSecret)}&HOOK_URL=`;

    // Load clients from Firestore
    const loadClients = async () => {
        try {
            const clientsRef = collection(db, 'punchout_clients');
            const q = query(clientsRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            
            const clientsList = [];
            snapshot.forEach(doc => {
                clientsList.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            
            setClients(clientsList);
        } catch (error) {
            console.error('Error loading clients:', error);
            showSnackbar('Fehler beim Laden der Clients', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Load users for email binding
    const loadUsers = async () => {
        try {
            const { apiFetch } = await import('../hooks/useAuth');
            const response = await apiFetch('/app/users');
            
            if (response.ok) {
                const data = await response.json();
                setUsers(data.users || []);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    useEffect(() => {
        if (claims?.role === 'SuperAdmin') {
            loadClients();
            loadUsers();
            loadProfileSecret();
        }
    }, [claims]);

    const loadProfileSecret = async () => {
        setLoadingProfile(true);
        try {
            const { apiFetch } = await import('../hooks/useAuth');
            const response = await apiFetch('/app/punchout/config/profile-secret');
            if (response.ok) {
                const data = await response.json();
                setProfileSecret(data.sharedSecret || '');
                setProfileUrls({
                    profileUrl: data.profileUrl || `${firebaseConfig.siteUrl}/cxml/profilerequest`,
                    punchoutSetupUrl: data.punchoutSetupUrl || `${firebaseConfig.siteUrl}/app/punchout/initialize`
                });
            }
        } catch (error) {
            console.error('Error loading profile secret:', error);
            showSnackbar('Fehler beim Laden des SAP-Profil-Passworts', 'error');
        } finally {
            setLoadingProfile(false);
        }
    };

    const handleSaveProfileSecret = async () => {
        if (!profileSecret.trim() || profileSecret.trim().length < 8) {
            showSnackbar('Passwort muss mindestens 8 Zeichen haben', 'error');
            return;
        }

        setSavingProfile(true);
        try {
            const { apiFetch } = await import('../hooks/useAuth');
            const response = await apiFetch('/app/punchout/config/profile-secret', {
                method: 'POST',
                body: JSON.stringify({ sharedSecret: profileSecret.trim() })
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                showSnackbar('SAP-Profil-Passwort gespeichert', 'success');
            } else {
                showSnackbar(data.message || 'Speichern fehlgeschlagen', 'error');
            }
        } catch (error) {
            console.error('Error saving profile secret:', error);
            showSnackbar('Fehler beim Speichern', 'error');
        } finally {
            setSavingProfile(false);
        }
    };

    // Generate strong shared secret
    const generateSharedSecret = () => {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        const secret = btoa(String.fromCharCode.apply(null, array))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
        return secret.substring(0, 32);
    };

    const handleOpenDialog = (client = null) => {
        if (client) {
            // Edit mode
            setEditingClient(client);
            setFormData({
                clientId: client.clientId || '',
                sharedSecret: client.sharedSecret || '',
                accountID: client.accountID || '',
                domain: client.domain || '',
                name: client.name || '',
                active: client.active !== false,
                userEmail: client.userEmail || ''
            });
        } else {
            // Create mode
            setEditingClient(null);
            const newSecret = generateSharedSecret();
            setFormData({
                clientId: '',
                sharedSecret: newSecret,
                accountID: '',
                domain: '',
                name: '',
                active: true,
                userEmail: ''
            });
        }
        setErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingClient(null);
        setErrors({});
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.clientId.trim()) {
            newErrors.clientId = 'Client ID ist erforderlich';
        } else if (!/^[A-Za-z0-9._-]+$/.test(formData.clientId.trim())) {
            newErrors.clientId = 'Nur Buchstaben, Zahlen, Punkt, Unter-/Bindestrich erlaubt';
        }
        
        if (!formData.sharedSecret.trim()) {
            newErrors.sharedSecret = 'Shared Secret ist erforderlich';
        } else if (formData.sharedSecret.length < 20) {
            newErrors.sharedSecret = 'Mindestens 20 Zeichen erforderlich';
        }
        
        if (!formData.accountID.trim()) {
            newErrors.accountID = 'Kundennummer ist erforderlich';
        }
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name ist erforderlich';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }
        
        try {
            const clientData = {
                clientId: formData.clientId.trim(),
                sharedSecret: formData.sharedSecret,
                accountID: formData.accountID.trim(),
                domain: formData.domain.trim(),
                name: formData.name.trim(),
                active: formData.active,
                userEmail: formData.userEmail.trim(),
                updatedAt: Timestamp.now()
            };
            
            if (editingClient) {
                // Update existing client
                const clientRef = doc(db, 'punchout_clients', editingClient.id);
                await updateDoc(clientRef, clientData);
                showSnackbar('Client erfolgreich aktualisiert', 'success');
            } else {
                // Create new client - use clientId as document ID
                clientData.createdAt = Timestamp.now();
                const clientRef = doc(db, 'punchout_clients', clientData.clientId);
                await setDoc(clientRef, clientData);
                showSnackbar('Client erfolgreich erstellt', 'success');
            }
            
            handleCloseDialog();
            loadClients();
        } catch (error) {
            console.error('Error saving client:', error);
            showSnackbar(`Fehler: ${error.message}`, 'error');
        }
    };

    const handleDelete = async (client) => {
        if (!window.confirm(`Möchten Sie den Client "${client.name}" wirklich löschen?`)) {
            return;
        }
        
        try {
            await deleteDoc(doc(db, 'punchout_clients', client.id));
            showSnackbar('Client erfolgreich gelöscht', 'success');
            loadClients();
        } catch (error) {
            console.error('Error deleting client:', error);
            showSnackbar(`Fehler: ${error.message}`, 'error');
        }
    };

    const handleCopyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        showSnackbar(`${label} in Zwischenablage kopiert`, 'success');
    };

    const toggleShowSecret = (clientId) => {
        setShowSecret(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleUserSelect = (e) => {
        const email = e.target.value;
        setFormData(prev => ({ ...prev, userEmail: email }));
        
        // Auto-fill account ID if user has one
        const selectedUser = users.find(u => u.email === email);
        if (selectedUser?.customClaims?.aid) {
            setFormData(prev => ({ 
                ...prev, 
                accountID: selectedUser.customClaims.aid,
                name: selectedUser.displayName || email
            }));
        }
    };

    // Check permissions
    if (!user || claims?.role !== 'SuperAdmin') {
        return (
            <BasicPage>
                <Alert severity="error">
                    Zugriff verweigert. Nur für SuperAdmins.
                </Alert>
            </BasicPage>
        );
    }

    return (
        <BasicPage title="Punchout Client Management" icon={<People />}>
            <Stack spacing={3}>
                {/* Info Alert */}
                <Alert severity="info">
                    <Typography variant="subtitle2" gutterBottom>
                        Client-Verwaltung
                    </Typography>
                    <Typography variant="body2">
                        Hier können Sie Punchout-Clients für Ihre Kunden erstellen und verwalten. 
                        Jeder Client erhält eine eindeutige Client-ID und ein Shared Secret für die Authentifizierung.
                    </Typography>
                </Alert>

                {/* SAP Business Network profile password */}
                <Card>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <VpnKey />
                            <Typography variant="h6">
                                SAP Business Network – Profil-Passwort
                            </Typography>
                        </Stack>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                Dieses <strong>Gemeinsame Passwort</strong> muss identisch in SAP Business Network
                                (cXML-Einrichtung) und hier hinterlegt sein. Es wird für cXML ProfileRequest verwendet.
                            </Typography>
                        </Alert>
                        <Stack spacing={2}>
                            <TextField
                                label="Profil-URL (für SAP)"
                                value={profileUrls.profileUrl}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                helperText="Diese URL in SAP unter „Profil-URL“ eintragen"
                            />
                            <TextField
                                label="Punchout-URL"
                                value={profileUrls.punchoutSetupUrl}
                                fullWidth
                                InputProps={{ readOnly: true }}
                                helperText="Wird automatisch in der ProfileResponse an SAP zurückgegeben"
                            />
                            <TextField
                                label="Gemeinsames Passwort"
                                type={showProfileSecret ? 'text' : 'password'}
                                value={profileSecret}
                                onChange={(e) => setProfileSecret(e.target.value)}
                                fullWidth
                                disabled={loadingProfile}
                                helperText="Mindestens 8 Zeichen – identisch mit SAP Business Network"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowProfileSecret(prev => !prev)}
                                                edge="end"
                                            >
                                                {showProfileSecret ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                            <Tooltip title="Neues Passwort generieren">
                                                <IconButton
                                                    onClick={() => setProfileSecret(generateSharedSecret())}
                                                    edge="end"
                                                >
                                                    <Refresh />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Passwort kopieren">
                                                <IconButton
                                                    onClick={() => handleCopyToClipboard(profileSecret, 'Profil-Passwort')}
                                                    edge="end"
                                                    disabled={!profileSecret}
                                                >
                                                    <ContentCopy />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={handleSaveProfileSecret}
                                    disabled={loadingProfile || savingProfile}
                                >
                                    {savingProfile ? 'Speichern…' : 'Profil-Passwort speichern'}
                                </Button>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Clients ({clients.length})
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                    >
                        Neuen Client erstellen
                    </Button>
                </Box>

                {/* Clients Table */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Client ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>User E-Mail</TableCell>
                                <TableCell>Kundennummer</TableCell>
                                <TableCell>Domain</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Shared Secret</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        Laden...
                                    </TableCell>
                                </TableRow>
                            ) : clients.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        Keine Clients vorhanden. Erstellen Sie einen neuen Client.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                clients.map((client) => (
                                    <TableRow key={client.id}>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {client.clientId}
                                                </Typography>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleCopyToClipboard(client.clientId, 'Client ID')}
                                                >
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{client.name}</TableCell>
                                        <TableCell>{client.userEmail || '-'}</TableCell>
                                        <TableCell>{client.accountID}</TableCell>
                                        <TableCell>{client.domain || '-'}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={client.active !== false ? 'Aktiv' : 'Inaktiv'}
                                                color={client.active !== false ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    {showSecret[client.id] 
                                                        ? client.sharedSecret 
                                                        : '••••••••••••••••'
                                                    }
                                                </Typography>
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => toggleShowSecret(client.id)}
                                                >
                                                    {showSecret[client.id] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleCopyToClipboard(client.sharedSecret, 'Shared Secret')}
                                                >
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Bearbeiten">
                                                    <IconButton 
                                                        size="small"
                                                        onClick={() => handleOpenDialog(client)}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Löschen">
                                                    <IconButton 
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(client)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Create/Edit Dialog */}
                <Dialog 
                    open={openDialog} 
                    onClose={handleCloseDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        {editingClient ? 'Client bearbeiten' : 'Neuen Client erstellen'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            {/* User Selection */}
                            <TextField
                                select
                                label="User E-Mail (optional)"
                                value={formData.userEmail}
                                onChange={handleUserSelect}
                                fullWidth
                                SelectProps={{
                                    native: true
                                }}
                                helperText="Wählen Sie einen User, um automatisch Kundennummer und Name zu übernehmen"
                            >
                                <option value="">-- Kein User --</option>
                                {users.map(user => (
                                    <option key={user.uid} value={user.email}>
                                        {user.email} ({user.displayName})
                                    </option>
                                ))}
                            </TextField>

                            <TextField
                                label="Client ID"
                                value={formData.clientId}
                                onChange={(e) => setFormData(prev => ({ ...prev, clientId: e.target.value }))}
                                error={!!errors.clientId}
                                helperText={errors.clientId || 'Eindeutige Kennung. Für Ariba bitte die Network ID exakt verwenden (z.B. AN11214354826-T). Groß-/Kleinschreibung wird beibehalten.'}
                                fullWidth
                                disabled={!!editingClient}
                            />

                            <TextField
                                label="Name"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                error={!!errors.name}
                                helperText={errors.name || 'Anzeigename des Clients'}
                                fullWidth
                            />

                            <TextField
                                label="Shared Secret"
                                value={formData.sharedSecret}
                                onChange={(e) => setFormData(prev => ({ ...prev, sharedSecret: e.target.value }))}
                                error={!!errors.sharedSecret}
                                helperText={errors.sharedSecret || 'Mindestens 20 Zeichen'}
                                fullWidth
                                type="password"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Neu generieren">
                                                <IconButton
                                                    onClick={() => setFormData(prev => ({ ...prev, sharedSecret: generateSharedSecret() }))}
                                                >
                                                    <Refresh />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Kopieren">
                                                <IconButton
                                                    onClick={() => handleCopyToClipboard(formData.sharedSecret, 'Shared Secret')}
                                                >
                                                    <ContentCopy />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <TextField
                                label="Kundennummer (Account ID)"
                                value={formData.accountID}
                                onChange={(e) => setFormData(prev => ({ ...prev, accountID: e.target.value }))}
                                error={!!errors.accountID}
                                helperText={errors.accountID || 'Kundennummer in Ihrem System'}
                                fullWidth
                            />

                            <TextField
                                label="Domain (optional)"
                                value={formData.domain}
                                onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                                helperText="Domain des Kunden (z.B. kunde.ivalua.com)"
                                fullWidth
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.active}
                                        onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                                    />
                                }
                                label="Client aktiv"
                            />

                            {/* Credentials Preview */}
                            {formData.clientId && formData.sharedSecret && (
                                <Alert severity="success">
                                    <Typography variant="subtitle2" gutterBottom>
                                        Zugangsdaten für den Kunden:
                                    </Typography>
                                    <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                        <strong>Punchout URL:</strong> {punchoutInitializeUrl}<br/>
                                        <strong>Simple Systems OCI:</strong> {simpleSystemsOciUrl(formData.clientId, formData.sharedSecret)}<br/>
                                        <strong>Client ID:</strong> {formData.clientId}<br/>
                                        <strong>Shared Secret:</strong> {formData.sharedSecret}<br/>
                                        <strong>Kundennummer:</strong> {formData.accountID}
                                    </Typography>
                                </Alert>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>
                            Abbrechen
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleSave}
                        >
                            {editingClient ? 'Aktualisieren' : 'Erstellen'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    message={snackbar.message}
                />
            </Stack>
        </BasicPage>
    );
};

